// @ts-ignore shaka player lacks typing https://github.com/shaka-project/shaka-player/issues/1030
import shaka from 'shaka-player/dist/shaka-player.compiled.debug';
import { loggerService } from 'libs/logger';
import Player from './player';
import { shakaPlayerConfig } from './shaka-config';
const GAP_NEAR_SEEK_THRESHOLD_SEC = 5;
const RECENT_SEEK_THRESHOLD_MS = 3000;
let shakaLogWrapped = false;
let activeShakaLogContext = null;
let activeShakaVideoElement = null;
const formatBufferedRanges = (buffered) => {
    const ranges = [];
    for (let i = 0; i < buffered.length; i += 1) {
        ranges.push(`[${buffered.start(i).toFixed(3)}, ${buffered.end(i).toFixed(3)}]`);
    }
    return ranges.join(', ');
};
const getPlaybackSnapshot = (videoElement) => {
    return {
        currentTime: Number(videoElement.currentTime.toFixed(3)),
        paused: videoElement.paused ? 1 : 0,
        playbackRate: Number(videoElement.playbackRate.toFixed(3)),
        readyState: videoElement.readyState,
        networkState: videoElement.networkState,
        bufferedRanges: formatBufferedRanges(videoElement.buffered),
    };
};
const safeToString = (value) => {
    if (typeof value !== 'object' || value === null) {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    }
    catch (_a) {
        return '[Unserializable value]';
    }
};
const formatShakaArgs = (...args) => args.map((arg) => safeToString(arg)).join(' ');
// NOTE:  Gap related logging is just for debugging purposes, can be deleted later
const parseGapFromMessage = (message) => {
    const ignoreMatch = message.match(/Ignoring large gap at ([\d.]+) size ([\d.]+)/i);
    if (ignoreMatch) {
        return { gapPosition: Number(ignoreMatch[1]), gapSize: Number(ignoreMatch[2]) };
    }
    const jumpMatch = message.match(/Jumping forward ([\d.]+) seconds because of gap (?:before start time of|starting at) ([\d.]+)/i);
    if (jumpMatch) {
        return { gapSize: Number(jumpMatch[1]), gapPosition: Number(jumpMatch[2]) };
    }
    return {};
};
const isGapRelatedMessage = (message) => /gap|large gap|jumping forward/i.test(message);
const isJumpedGapMessage = (message) => /Jumping forward [\d.]+ seconds because of gap/i.test(message);
const isIgnoredGapMessage = (message) => /Ignoring large gap at [\d.]+ size [\d.]+/i.test(message);
const getGapContextData = (message) => {
    if (!activeShakaLogContext) {
        return {};
    }
    const gapInfo = parseGapFromMessage(message);
    const data = {
        projectId: activeShakaLogContext.projectId,
        projectName: activeShakaLogContext.projectName,
        lastSeekTarget: activeShakaLogContext.lastSeekTarget,
        seeksCausingGapCount: activeShakaLogContext.seeksCausingGapCount,
    };
    if (gapInfo.gapPosition === undefined) {
        return data;
    }
    const now = Date.now();
    const timeSinceSeek = now - activeShakaLogContext.lastSeekTime;
    const distance = Math.abs(gapInfo.gapPosition - activeShakaLogContext.lastSeekTarget);
    const gapNearSeek = distance < GAP_NEAR_SEEK_THRESHOLD_SEC;
    const seekWasRecent = timeSinceSeek < RECENT_SEEK_THRESHOLD_MS;
    const seekLikelyCausedGap = gapNearSeek && seekWasRecent;
    if (seekLikelyCausedGap) {
        activeShakaLogContext.seeksCausingGapCount += 1;
        data.seeksCausingGapCount = activeShakaLogContext.seeksCausingGapCount;
    }
    data.gapPosition = gapInfo.gapPosition;
    data.seekLikelyCausedGap = seekLikelyCausedGap ? 1 : 0;
    if (gapInfo.gapSize !== undefined) {
        data.gapSize = gapInfo.gapSize;
        if (isJumpedGapMessage(message)) {
            activeShakaLogContext.jumpedLargeGapCount += 1;
            activeShakaLogContext.jumpedLargeGapDurationTotalSec += gapInfo.gapSize;
            data.jumpedLargeGapCount = activeShakaLogContext.jumpedLargeGapCount;
            data.jumpedLargeGapDurationTotalSec = Number(activeShakaLogContext.jumpedLargeGapDurationTotalSec.toFixed(3));
        }
        else if (isIgnoredGapMessage(message)) {
            activeShakaLogContext.ignoredLargeGapCount += 1;
            activeShakaLogContext.ignoredLargeGapDurationTotalSec += gapInfo.gapSize;
            data.ignoredLargeGapCount = activeShakaLogContext.ignoredLargeGapCount;
            data.ignoredLargeGapDurationTotalSec = Number(activeShakaLogContext.ignoredLargeGapDurationTotalSec.toFixed(3));
        }
    }
    return data;
};
const wrapShakaLogForBeeyLogger = () => {
    if (shakaLogWrapped)
        return;
    shakaLogWrapped = true;
    const logger = loggerService.getRoot().componentLogger('shaka-player');
    const wrap = (original, forwardAlways, level) => {
        return (...args) => {
            const message = formatShakaArgs(...args);
            const isGapRelated = isGapRelatedMessage(message);
            if (forwardAlways || isGapRelated) {
                const data = Object.assign({ source: 'shaka', message }, getGapContextData(message));
                if (isGapRelated && activeShakaVideoElement !== null) {
                    Object.assign(data, getPlaybackSnapshot(activeShakaVideoElement));
                    // eslint-disable-next-line no-console
                    console.warn('[shaka-gap]', message, data);
                }
                logger[level]('shaka: {message}', data);
            }
            // Keep native Shaka console output only for warnings/errors.
            if (level === 'warn' || level === 'error') {
                original.call(shaka.log, ...args);
            }
        };
    };
    shaka.log.error = wrap(shaka.log.error, true, 'error');
    shaka.log.warning = wrap(shaka.log.warning, false, 'warn');
    shaka.log.info = wrap(shaka.log.info, false, 'info');
    shaka.log.debug = wrap(shaka.log.debug, false, 'info');
    shaka.log.v1 = wrap(shaka.log.v1, false, 'info');
    shaka.log.v2 = wrap(shaka.log.v2, false, 'info');
};
export default class ShakaPlayer extends Player {
    constructor(domElement, playback, onReadyToPlay, manifestURL, connection, project) {
        var _a, _b;
        super(domElement, playback, onReadyToPlay);
        this.logger = loggerService.getRoot().componentLogger('shaka-player');
        this.onLoadedMetadata = (event, initTime) => {
            const loadTime = new Date().getTime() - initTime;
            this.logger.info('shaka player loaded', {
                loadTime: loadTime / 1000,
                projectName: this.project.description.name,
                source: 'shaka',
            });
            this.onReadyToPlay(this.shaka.isAudioOnly() === false);
            this.playback.changeDuration(this.shaka.seekRange().end);
        };
        this.startDurationPolling = () => {
            this.stopDurationPolling();
            this.durationPollTimer = window.setInterval(() => {
                this.playback.changeDuration(this.shaka.seekRange().end);
                if (this.shaka.isLive() === false) {
                    this.stopDurationPolling();
                }
            }, 1000);
        };
        this.stopDurationPolling = () => {
            if (this.durationPollTimer !== null) {
                clearInterval(this.durationPollTimer);
                this.durationPollTimer = null;
            }
        };
        this.durationPollTimer = null;
        this.videoElement = domElement;
        this.shaka = new shaka.Player(domElement);
        this.shaka.configure(shakaPlayerConfig);
        const streamingConfig = (_b = (_a = this.shaka.getConfiguration()) === null || _a === void 0 ? void 0 : _a.streaming) !== null && _b !== void 0 ? _b : {};
        this.logger.info('shaka streaming config applied', {
            source: 'shaka',
            projectId: project.id,
            projectName: project.description.name,
            jumpLargeGaps: streamingConfig.jumpLargeGaps,
            gapDetectionThreshold: streamingConfig.gapDetectionThreshold,
            smallGapLimit: streamingConfig.smallGapLimit,
            stallEnabled: streamingConfig.stallEnabled,
            stallThreshold: streamingConfig.stallThreshold,
            stallSkip: streamingConfig.stallSkip,
            safeSeekOffset: streamingConfig.safeSeekOffset,
        });
        this.project = project;
        this.seekStats = {
            projectId: project.id,
            projectName: project.description.name,
            lastSeekTarget: 0,
            lastSeekTime: 0,
            seeksCausingGapCount: 0,
            seekRequestedCount: 0,
            seekCompletedCount: 0,
            seekDurationTotalMs: 0,
            waitingEventCount: 0,
            stalledEventCount: 0,
            jumpedLargeGapCount: 0,
            ignoredLargeGapCount: 0,
            jumpedLargeGapDurationTotalSec: 0,
            ignoredLargeGapDurationTotalSec: 0,
        };
        this.lastKnownPlaybackTime = this.videoElement.currentTime;
        this.pendingSeekFrom = null;
        this.pendingSeekStartedAt = null;
        activeShakaLogContext = this.seekStats;
        activeShakaVideoElement = this.videoElement;
        this.boundHandleSeeking = () => {
            const seekFrom = this.lastKnownPlaybackTime;
            const seekTo = this.videoElement.currentTime;
            const seekTime = Date.now();
            this.pendingSeekFrom = seekFrom;
            this.pendingSeekStartedAt = seekTime;
            this.seekStats.lastSeekTarget = seekTo;
            this.seekStats.lastSeekTime = seekTime;
            this.seekStats.seekRequestedCount += 1;
            this.logger.info('shaka seek requested', {
                source: 'shaka',
                projectId: this.seekStats.projectId,
                projectName: this.seekStats.projectName,
                seekFrom,
                seekTo,
            });
            // eslint-disable-next-line no-console
            console.info('[shaka] seek requested', { seekFrom, seekTo });
        };
        this.boundHandleSeeked = () => {
            var _a;
            const seekTo = this.videoElement.currentTime;
            const seekFrom = (_a = this.pendingSeekFrom) !== null && _a !== void 0 ? _a : this.lastKnownPlaybackTime;
            const seekDuration = this.pendingSeekStartedAt !== null
                ? Date.now() - this.pendingSeekStartedAt
                : undefined;
            this.seekStats.lastSeekTarget = seekTo;
            this.seekStats.lastSeekTime = Date.now();
            this.lastKnownPlaybackTime = seekTo;
            this.pendingSeekFrom = null;
            this.pendingSeekStartedAt = null;
            this.seekStats.seekCompletedCount += 1;
            if (seekDuration !== undefined) {
                this.seekStats.seekDurationTotalMs += seekDuration;
            }
            this.logger.info('shaka seek completed', {
                source: 'shaka',
                projectId: this.seekStats.projectId,
                projectName: this.seekStats.projectName,
                seekFrom,
                seekTo,
                seekDurationMs: seekDuration !== null && seekDuration !== void 0 ? seekDuration : 'unknown',
            });
            // eslint-disable-next-line no-console
            console.info('[shaka] seek completed', {
                seekFrom,
                seekTo,
                seekDurationMs: seekDuration !== null && seekDuration !== void 0 ? seekDuration : 'unknown',
            });
        };
        this.boundHandleTimeUpdate = () => {
            this.lastKnownPlaybackTime = this.videoElement.currentTime;
        };
        this.videoElement.addEventListener('seeking', this.boundHandleSeeking);
        this.videoElement.addEventListener('seeked', this.boundHandleSeeked);
        this.videoElement.addEventListener('timeupdate', this.boundHandleTimeUpdate);
        this.boundHandleWaiting = () => {
            this.seekStats.waitingEventCount += 1;
            this.logger.info('shaka html-video waiting', Object.assign({ source: 'shaka', projectId: this.seekStats.projectId, projectName: this.seekStats.projectName }, getPlaybackSnapshot(this.videoElement)));
        };
        this.boundHandleStalled = () => {
            this.seekStats.stalledEventCount += 1;
            this.logger.info('shaka html-video stalled', Object.assign({ source: 'shaka', projectId: this.seekStats.projectId, projectName: this.seekStats.projectName }, getPlaybackSnapshot(this.videoElement)));
        };
        this.videoElement.addEventListener('waiting', this.boundHandleWaiting);
        this.videoElement.addEventListener('stalled', this.boundHandleStalled);
        shaka.log.setLevel(shaka.log.Level.V1);
        wrapShakaLogForBeeyLogger();
        this.shaka.addEventListener('error', (e) => {
            var _a, _b, _c, _d, _e;
            const error = (_a = e === null || e === void 0 ? void 0 : e.detail) !== null && _a !== void 0 ? _a : e;
            this.logger.error('shaka error', Object.assign({ event: safeToString(error), shakaErrorCode: (_b = error === null || error === void 0 ? void 0 : error.code) !== null && _b !== void 0 ? _b : 'unknown', shakaErrorSeverity: (_c = error === null || error === void 0 ? void 0 : error.severity) !== null && _c !== void 0 ? _c : 'unknown', shakaErrorCategory: (_d = error === null || error === void 0 ? void 0 : error.category) !== null && _d !== void 0 ? _d : 'unknown', shakaErrorData: safeToString((_e = error === null || error === void 0 ? void 0 : error.data) !== null && _e !== void 0 ? _e : []), lastSeekTarget: this.seekStats.lastSeekTarget, seeksCausingGapCount: this.seekStats.seeksCausingGapCount, projectId: this.seekStats.projectId, projectName: this.seekStats.projectName, source: 'shaka' }, getPlaybackSnapshot(this.videoElement)));
        });
        this.shaka.load(manifestURL).then(() => {
            if (this.shaka.isLive() === true) {
                this.startDurationPolling();
            }
        });
    }
    destroy() {
        this.videoElement.removeEventListener('seeking', this.boundHandleSeeking);
        this.videoElement.removeEventListener('seeked', this.boundHandleSeeked);
        this.videoElement.removeEventListener('timeupdate', this.boundHandleTimeUpdate);
        this.videoElement.removeEventListener('waiting', this.boundHandleWaiting);
        this.videoElement.removeEventListener('stalled', this.boundHandleStalled);
        const avgSeekDurationMs = this.seekStats.seekCompletedCount > 0
            ? this.seekStats.seekDurationTotalMs / this.seekStats.seekCompletedCount
            : 0;
        this.logger.info('shaka session summary', {
            seeksCausingGapCount: this.seekStats.seeksCausingGapCount,
            seekRequestedCount: this.seekStats.seekRequestedCount,
            seekCompletedCount: this.seekStats.seekCompletedCount,
            avgSeekDurationMs: Number(avgSeekDurationMs.toFixed(1)),
            waitingEventCount: this.seekStats.waitingEventCount,
            stalledEventCount: this.seekStats.stalledEventCount,
            jumpedLargeGapCount: this.seekStats.jumpedLargeGapCount,
            ignoredLargeGapCount: this.seekStats.ignoredLargeGapCount,
            jumpedLargeGapDurationTotalSec: Number(this.seekStats.jumpedLargeGapDurationTotalSec.toFixed(3)),
            ignoredLargeGapDurationTotalSec: Number(this.seekStats.ignoredLargeGapDurationTotalSec.toFixed(3)),
            projectId: this.seekStats.projectId,
            projectName: this.seekStats.projectName,
            source: 'shaka',
        });
        super.destroy();
        this.stopDurationPolling();
        if (activeShakaLogContext === this.seekStats) {
            activeShakaLogContext = null;
        }
        if (activeShakaVideoElement === this.videoElement) {
            activeShakaVideoElement = null;
        }
        this.shaka.destroy();
    }
}
